import { useEffect, useMemo, useState } from 'react';
import Button from 'components/commercetools-ui/atoms/button';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import { Reference } from 'types/reference';
import Image, { ImageProps } from 'frontastic/lib/image';
import Slider from 'components/commercetools-ui/atoms/slider';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useWindowWidth from 'helpers/hooks/useBrowerWidth';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import {
  desktop,
  extraLargeDesktop,
  hugeDesktop,
  largeDesktop,
  mediumDesktop,
  semiLargeDesktop,
  tablet
} from 'helpers/utils/screensizes';
import { StaticImageData } from 'next/image';

export interface ImageSliderProps {
  data: any;
}

const ImageContentSlider: React.FC<ImageSliderProps> = ({ data }) => {
  const { isTouchDevice } = useTouchDevice();
  const [isHugeDesktop2Size] = useMediaQuery(extraLargeDesktop);
  const [isLargeDesktopSize] = useMediaQuery(largeDesktop);
  const [isMediumDesktopSize] = useMediaQuery(mediumDesktop);
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isTabletSize] = useMediaQuery(tablet);
  const [isHugeDesktopSize] = useMediaQuery(hugeDesktop)
  const [isMediumDesktop2Size] = useMediaQuery(semiLargeDesktop);
  const [maxHeight, setMaxHeight] = useState(0);

  const width = useWindowWidth();

  const showArrows = useMemo(
    () =>
      !isTouchDevice &&
      !(
        (isLargeDesktopSize && data?.banner_slider?.length <= 4) ||
        (!isLargeDesktopSize && data?.banner_slider?.length <= 2)
      ),
    [isLargeDesktopSize, isTouchDevice, data?.banner_slider?.length],
  );

  const cmsCssClass = data?.additional_css;

  useEffect(() => {
    const updateHeight = () => {
      const slideElements = document.querySelectorAll(".slide-content");
      let maxHeight = 0;
      
      slideElements.forEach((slide) => {
        const height = slide?.clientHeight || 0;
        maxHeight = Math.max(maxHeight, height);
      });
  
      setMaxHeight(maxHeight);
    };
  
    updateHeight();
  
    if (typeof window !== "undefined") {
      window.addEventListener("resize", updateHeight);
    }
  
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", updateHeight);
      }
    };
  }, [isDesktopSize, data]);
  

  return (
    <div className={`${cmsCssClass} image-content w-full ${isDesktopSize ? 'padding-l-0 padding-b-0': 'padding-l-24 padding-b-24'}`}>
      <Slider
        containerClassName={showArrows ? 'padding-x-48-sm' : ''}
        slidesPerView={isDesktopSize ? 1 : 1.15}
        spaceBetween={8}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
        }}
        dots={false}
        arrows={isDesktopSize}
        allowArrowsOnTouchDevice
        innerArrows
        allowTouchMove={true}
        prevButtonStyles={{
          top: '90%',
          left: isHugeDesktop2Size ? '90%' : isHugeDesktopSize ? '87%' : isLargeDesktopSize ? '87%' : isMediumDesktop2Size ? '87%' : isMediumDesktopSize ? '87%' : isDesktopSize ? '87%' :  width !== null && width == 1024 ? 650 : 500,
          padding: isHugeDesktop2Size ? '20px' : '10px',
        }}
        nextButtonStyles={{
          top: '90%',
          left: isHugeDesktop2Size ? '93.3%' : isHugeDesktopSize ? '93%' : isLargeDesktopSize ? '91.9%' : isMediumDesktop2Size ? '92.8%' :  isMediumDesktopSize ? '92.3%' : isDesktopSize ? '91.7%' :  width !== null && width == 1024 ? 650 : 500,
          padding: isHugeDesktop2Size ? '20px' : '10px',
        }}
      >
        {data?.banner_slider?.map(
          (slide: {
            additional_css: any;
            ctas: any;
            banner_desktop_image: { url: string | StaticImageData | undefined; title: string | undefined; description: string | undefined };
            banner_heading: string | undefined;
            banner_description: string | undefined;
            call_to_action: { title: string | undefined; href: string | Reference | undefined };
          }) => (
            <div key={slide.banner_heading} className={`${width !== null && width > 1023 ? `${slide?.additional_css} flex` : `${slide?.additional_css} flex flex-col`}`}>
              <div className="xl:w-[60%] lg:w-[60%] sm:w-full md:w-full">
                  <Image
                    loading='lazy'
                    src={slide?.banner_desktop_image?.url}
                    alt={slide?.banner_desktop_image?.description}
                    style={{
                      height: '115%',
                      width: `${isTabletSize ?  '1600px' : '750px'}`
                    }}
                    className="brightness-75"
                  />
              </div>
              <div className="bg-color xl:w-[40%] lg:w-[40%] sm:w-full md:w-full slide-content">
                <div className="flex-1">
                  {isDesktopSize ? (
                    <div
                      className={` content-div-padding-lg padding-l-lg-40 content-div-padding-xl content-div-padding-2xl padding-x-md padding-b-lg-50 padding-l-lg-78
                       content-div-padding-lg content-div-padding-xl content-div-padding-2xl`}
                       style={{minHeight: maxHeight}}
                    >
                      <Typography
                          as="h1"
                          className="print:text-xl text-left text-24 font-medium leading-[3.25rem] text-[#090909] xl:text-36 2xl:text-48 relative"
                        >
                          {slide?.banner_heading}
                        </Typography>
                      <div className="xl:w-[78%] 2xl:w-[65%]">
                        <Typography className="mt-22 text-left text-16 font-normal leading-8 line-clamp-4 text-[#333] relative">
                          {slide?.banner_description}
                        </Typography>
                      </div>
                      {slide?.ctas &&
                        slide?.ctas.map((cta: any) => (
                          <Link link={cta?.button?.href} key={cta?.button?.title} target={cta?.button?.href?.includes("https://")?'_blank' : ''}>
                            <Button
                              style={{
                                backgroundColor: cta?.button_background_color?.hex,
                                border: `1px solid ${cta?.button_border_color?.hex}`,
                                color: cta?.button_text_color?.hex,
                                cursor: 'pointer',
                              }}
                              className={`margin-t-48 border-none padding-x-32-sm padding-y-18-sm shadow-sm cursor-button ${
                                cta?.button_background_color?.hex?.toLowerCase() === '#ffffff'
                                  ? 'hover:border-[#F4F4F4] hover:bg-[#F4F4F4]'
                                  : 'hover:bg-[#394A54]'
                              }`}
                            >
                              <Typography as="span" className="fontSize16-sm font-medium lh-sm text-white">
                                {cta?.button?.title}
                              </Typography>
                            </Button>
                          </Link>
                        ))}
                    </div>
                  ) : (
                      <div className="flex-1 bg-color padding-24-sm">
                    <div className=''>
                      <Typography as="h1" className="text-left font-size24-sm font-medium lh-1 text-[#090909]">
                        {slide?.banner_heading}
                      </Typography>
                    </div>
                        <Typography className={`  image-content-min-height  mt-16 text-left fontSize16-sm font-normal lh-base text-[#090909] image-content-min-height `}>
                        {slide?.banner_description}
                      </Typography>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ),
          )}
        
      </Slider>
    </div>
  );
};

export default ImageContentSlider;
